import { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import { AggregateField } from '../../../../../../domain/entity/WbsItemEntity'
import {
  ProgramManagementGridContext,
  ProgramManagementRow,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from '../../../programManagement'
import { colorPalette } from '../../../../../style/colorPalette'

export type RowValueGetter = ValueGetterParams<ProgramManagementRow>
export type RowWithData = { data?: ProgramManagementRow | null }

export const GRAYED_OUT_STYLE = {
  backgroundColor: '#bdbdbd',
  color: colorPalette.monotone[3],
}

export const RIGHT_ALIGN_STYLE = { justifyContent: 'flex-end' }

export const isProcessRow = (params: RowWithData): boolean =>
  params.data?.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROCESS ||
  params.data?.type === PROGRAM_MANAGEMENT_ROW_TYPE.WORKGROUP

export const numericCellStyle = (params: RowWithData) =>
  isProcessRow(params)
    ? { ...GRAYED_OUT_STYLE, ...RIGHT_ALIGN_STYLE }
    : RIGHT_ALIGN_STYLE

export const numberValueFormatter = (params: {
  value: number | undefined | null
}) => {
  if (params.value == null) return ''
  return params.value.toLocaleString()
}

const formatDecimal = (value: number): string =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

const getGridContext = (
  params: ValueFormatterParams<ProgramManagementRow>
): ProgramManagementGridContext | undefined =>
  params.context as ProgramManagementGridContext | undefined

/**
 * 進捗列用: 個数モードは整数、工数モードは選択単位に変換して小数1桁。
 * 工数の値は時間(hour)単位で保持されている。
 */
export const progressValueFormatter = (
  params: ValueFormatterParams<ProgramManagementRow>
) => {
  if (params.value == null) return ''
  const ctx = getGridContext(params)
  const isWorkload = ctx?.aggregateField === AggregateField.WBS_ITEM_WORKLOAD
  if (isWorkload) {
    const hoursPerUnit = ctx?.workloadUnitState?.hoursPerSelectedUnit || 1
    return formatDecimal((params.value as number) / hoursPerUnit)
  }
  return (params.value as number).toLocaleString()
}

/**
 * 時間(hour)単位の値を選択した工数単位に変換して表示する。
 * 生産性指標(SV, CV, ETC, EAC)や投入工数(actualResource)向け。
 */
export const hoursValueFormatter = (
  params: ValueFormatterParams<ProgramManagementRow>
) => {
  if (params.value == null) return ''
  const ctx = getGridContext(params)
  const hoursPerUnit = ctx?.workloadUnitState?.hoursPerSelectedUnit || 1
  return formatDecimal((params.value as number) / hoursPerUnit)
}

/**
 * 人月(man-month)単位の値を選択した工数単位に変換して表示する。
 * リソース列(totalResource, investedResource, scheduledResource)向け。
 */
export const manMonthValueFormatter = (
  params: ValueFormatterParams<ProgramManagementRow>
) => {
  if (params.value == null) return ''
  const ctx = getGridContext(params)
  const wus = ctx?.workloadUnitState
  if (!wus) return formatDecimal(params.value as number)
  const hoursPerMonth = wus.dailyWorkHours * wus.monthlyWorkDays
  const converted =
    ((params.value as number) * hoursPerMonth) / wus.hoursPerSelectedUnit
  return formatDecimal(converted)
}

export const percentValueFormatter = (params: {
  value: number | undefined | null
}) => {
  if (params.value == null) return ''
  return `${(params.value * 100).toFixed(1)}%`
}
